<script setup lang="ts">
import { formatAnyDate } from '@/utils/helpers/dates';
import { orderIntentLabel, timeInForceLabel } from '@/modules/marketplace/helpers/marketplace';
import { OmsOrder } from '@/modules/marketplace/models';
import { useAgreementsStore } from '@/store/agreements-store';

const props = withDefaults(
  defineProps<{
    order: Partial<OmsOrder> & Pick<OmsOrder, 'side' | 'quantity' | 'security'>;
    asBroker?: boolean;
    hideRouting?: boolean;
    totalEligibleAgreements?: number;
  }>(),
  {
    asBroker: false,
    hideRouting: false,
    totalEligibleAgreements: undefined,
  }
);

const agreementsStore = useAgreementsStore();

function formatDate(value: Date): string {
  return formatAnyDate(value, 'MMM dd h:mm a');
}

function formatTime(value: Date): string {
  return formatAnyDate(value, 'h:mm a');
}
</script>

<template>
  <div class="summary-container">
    <div class="summary flex-grow-1">
      <div v-if="asBroker && order.company" class="item">
        <div>Company</div>
        <div>{{ order.company.companyName }} ({{ order.company.displayId }})</div>
      </div>
      <div class="item">
        <div>Security</div>
        <div>{{ order.security.ticker }} [{{ order.security.cusip }}]</div>
      </div>
      <div class="item">
        <div>Side</div>
        <div>
          <FormatSide :side="order.side" />
        </div>
      </div>
      <div class="item">
        <div>Fee</div>
        <div>
          <span v-if="order.fee === null">–</span>
          <RateOutput v-else :rate="order.fee" />
        </div>
      </div>
      <div class="item">
        <div>Rebate Rate</div>
        <div>
          <span v-if="order.rebate === null">–</span>
          <RateOutput v-else :rate="order.rebate" />
        </div>
      </div>

      <div class="item">
        <div>Quantity</div>
        <div>
          <PrettyNumber :value="order.quantity" />
          <span v-if="order.minQuantity && order.minQuantity > 1">
            &nbsp;(min. <PrettyNumber :value="order.minQuantity" />)
          </span>
        </div>
      </div>
      <div class="item">
        <div>Open Qty.</div>
        <div>
          <PrettyNumber v-if="order.filled" :value="order.quantity - order.filled" />
          <PrettyNumber v-else :value="order.quantity" />
        </div>
      </div>
      <div v-if="order.orderRef" class="item">
        <div>Filled Qty.</div>
        <div>
          <PrettyNumber v-if="order.filled" :value="order.filled" />
          <span v-else>–</span>
        </div>
      </div>
      <div v-if="order.orderRef" class="item">
        <div>IOI Qty.</div>
        <div>
          <PrettyNumber v-if="order.ioiQuantity" :value="order.ioiQuantity" />
          <span v-else>–</span>
        </div>
      </div>
      <div v-if="order.orderRef" class="item">
        <div>Firm Qty.</div>
        <div>
          <PrettyNumber v-if="order.firmQuantity" :value="order.firmQuantity" />
          <span v-else>–</span>
        </div>
      </div>
      <div v-if="order.orderRef" class="item">
        <div>Avg. Exec. Fee</div>
        <div>
          <RateOutput v-if="order.avgExecutionFee" :rate="order.avgExecutionFee" />
          <span v-else>–</span>
        </div>
      </div>
      <div v-if="order.orderRef" class="item">
        <div>Avg. Exec. Rebate</div>
        <div>
          <RateOutput v-if="order.avgExecutionRebate" :rate="order.avgExecutionRebate" />
          <span v-else>–</span>
        </div>
      </div>
      <div v-if="order.createdAt" class="item">
        <div>Created Time</div>
        <div>
          {{ formatDate(order.createdAt) }}
        </div>
      </div>
      <div class="item">
        <div>Agreements</div>
        <div
          v-if="
            order.agreements &&
            order.agreements.length > 0 &&
            props.totalEligibleAgreements !== undefined &&
            order.agreements.length === props.totalEligibleAgreements
          "
        >
          All
        </div>
        <div v-else-if="order.agreements && order.agreements.length > 0">
          <span v-for="(agreement, agreementIndex) in order.agreements" :key="agreement.id">
            <VTooltip bottom>
              <template #activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  {{ agreement.shortName
                  }}{{ agreementIndex === order.agreements.length - 1 ? '' : ',' }}
                </span>
              </template>
              <span>{{ agreementsStore.getAgreementDetails(agreement.id) }}</span>
            </VTooltip>
          </span>
        </div>
        <div v-else>Any</div>
      </div>
      <div v-if="order.timeInForceType" class="item">
        <div>Time in Force</div>
        <div>
          {{ timeInForceLabel(order.timeInForceType) }}
        </div>
      </div>
      <div v-if="order?.timeInForceType === 'GOOD_TILL_TIME' && order.goodTillTime" class="item">
        <div>Good Till Time</div>
        <div>
          {{ formatTime(order.goodTillTime) }}
        </div>
      </div>
      <div class="item">
        <div>Anonymous Order</div>
        <div>
          {{ order.isAnonymous ? 'Yes' : 'No' }}
        </div>
      </div>
      <div v-if="order.orderIntent" class="item">
        <div>Order Intent</div>
        <div>
          {{ orderIntentLabel(order.orderIntent) }}
        </div>
      </div>
      <div class="item">
        <div>Allow odd lots</div>
        <div>
          {{ order.allowOddLots ? 'Yes' : 'No' }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.grid {
  row-gap: 0 !important;

  // only showing the border when the both tables fit the grid
  & > *:nth-child(odd) {
    @container (width >= calc(250px + 1rem + 250px)) {
      border-right: 1px solid rgba(255, 255, 255, 0.12);
    }
  }
}

.summary-container {
  container: summary / inline-size;
  min-width: 100%;
  padding-block: 10px;

  .summary {
    width: min-content;
    height: 100%;
    max-height: 100%;
    column-width: auto;
    @container summary (width >= 440px) {
      column-width: 440px;
    }
    column-fill: auto;
    column-gap: 20px;
    line-height: 2;

    .item {
      display: grid;
      gap: 10px;
      grid-template-columns: 130px minmax(auto, 300px);
      @container summary (width >= 440px) {
        grid-template-columns: 130px 300px;
      }

      & > div {
        &:first-child {
          color: #888;
        }

        &:last-child {
          font-weight: bold;
          color: white;
        }
      }
    }
  }
}
</style>
